/**
 * Объединение стен в размерные цепочки и размеры, как их ставят на ремонтном чертеже.
 *
 * Если пользователь начертил одну прямую несколькими кликами, получается
 * несколько стен подряд — но подписывать каждую отдельно неправильно:
 * на чертеже это одна сторона, и размер у неё один.
 *
 * Объединяем через «проходные» узлы — те, где сходятся ровно две стены
 * и они лежат на одной прямой. Если в узел приходит третья стена
 * (например, перегородка), цепочка там разрывается: это осмысленная привязка,
 * и в настоящих чертежах размер в таком месте как раз разбивают.
 *
 * Мерить на ремонтном чертеже нужно то, что можно измерить рулеткой, а не оси стен:
 *   • внутри комнаты — каждая стена в чистоте, от угла до угла (`roomClearDimensions`);
 *   • снаружи — только габарит по наружным граням стен (`outerBounds`).
 */
import { dist, leftNormal, norm, pointInPolygon, sub, type Pt } from "@/shared/lib/geometry";
import type { WallGraph } from "@/shared/plan";
import type { Room } from "./rooms";
import type { WallOutline } from "./wallPolygons";

export interface DimensionSpan {
	id: string;
	from: Pt;
	to: Pt;
	wallIds: string[];
	/** Самая толстая стена цепочки — от неё считается отступ размерной линии */
	thickness: number;
}

/** Косинус угла, при котором стены считаем лежащими на одной прямой (около 1°). */
const COLLINEAR_DOT = -0.9998;

export const buildDimensionSpans = (plan: WallGraph): DimensionSpan[] => {
	const adjacency = new Map<string, string[]>();
	for (const wall of Object.values(plan.walls)) {
		for (const nodeId of [wall.a, wall.b]) {
			const list = adjacency.get(nodeId) ?? [];
			list.push(wall.id);
			adjacency.set(nodeId, list);
		}
	}

	/** Направление стены, выходящей из указанного узла. */
	const directionFrom = (nodeId: string, wallId: string): Pt | null => {
		const wall = plan.walls[wallId];
		if (!wall) return null;
		const other = wall.a === nodeId ? wall.b : wall.a;
		const from = plan.nodes[nodeId];
		const to = plan.nodes[other];
		if (!from || !to) return null;
		return norm(sub(to, from));
	};

	const isPassThrough = (nodeId: string): boolean => {
		const list = adjacency.get(nodeId);
		if (!list || list.length !== 2) return false;
		const first = directionFrom(nodeId, list[0]);
		const second = directionFrom(nodeId, list[1]);
		if (!first || !second) return false;
		return first.x * second.x + first.y * second.y <= COLLINEAR_DOT;
	};

	const visited = new Set<string>();
	const spans: DimensionSpan[] = [];

	for (const wall of Object.values(plan.walls)) {
		if (visited.has(wall.id)) continue;
		const chain: string[] = [wall.id];
		visited.add(wall.id);

		/** Идёт по прямой от узла, пока узлы остаются проходными. Возвращает конец цепочки. */
		const extend = (startNode: string, startWall: string): string => {
			let node = startNode;
			let previous = startWall;
			while (isPassThrough(node)) {
				const next = (adjacency.get(node) ?? []).find((id) => id !== previous);
				if (!next || visited.has(next)) break;
				visited.add(next);
				chain.push(next);
				const nextWall = plan.walls[next];
				node = nextWall.a === node ? nextWall.b : nextWall.a;
				previous = next;
			}
			return node;
		};

		const endA = extend(wall.a, wall.id);
		const endB = extend(wall.b, wall.id);
		const from = plan.nodes[endA];
		const to = plan.nodes[endB];
		if (!from || !to || dist(from, to) < 1) continue;

		spans.push({
			id: `span:${[...chain].toSorted().join("|")}`,
			from,
			to,
			wallIds: chain,
			thickness: Math.max(...chain.map((id) => plan.walls[id]?.thickness ?? 0)),
		});
	}

	return spans;
};

/* ─── Размеры в чистоте ─── */

/** Размер стороны: от `from` до `to`, выносится в сторону `normal`. */
export interface SideDimension {
	from: Pt;
	to: Pt;
	normal: Pt;
}

/** Короче этого сторону чистого контура не подписываем: это торец перегородки, а не стена. */
const CLEAR_MIN_LENGTH = 300;

/**
 * Размеры комнат в чистоте: каждая сторона внутреннего контура — своей длиной,
 * размерная линия внутри комнаты, вдоль стены.
 */
/**
 * Контур без точек посреди прямой стороны. Узел, в который снаружи упёрлась стена соседней
 * комнаты, лежит на стороне этой комнаты, но угла не образует: сторона подписывается одним
 * размером от угла до угла, а не двумя кусками.
 */
const cornersOnly = (poly: Pt[]): Pt[] =>
	poly.filter((point, i) => {
		const prev = poly[(i - 1 + poly.length) % poly.length];
		const next = poly[(i + 1) % poly.length];
		const a = sub(point, prev);
		const b = sub(next, point);
		const cross = a.x * b.y - a.y * b.x;
		return Math.abs(cross) > 1e-6 * Math.hypot(a.x, a.y) * Math.hypot(b.x, b.y);
	});

export const roomClearDimensions = (rooms: Room[]): SideDimension[] => {
	const dimensions: SideDimension[] = [];
	for (const room of rooms) {
		const poly = cornersOnly(room.inner);
		for (let i = 0; i < poly.length; i++) {
			const from = poly[i];
			const to = poly[(i + 1) % poly.length];
			if (dist(from, to) < CLEAR_MIN_LENGTH) continue;
			const normal = leftNormal(norm(sub(to, from)));
			const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
			const probe = { x: mid.x + normal.x * 20, y: mid.y + normal.y * 20 };
			const inward = pointInPolygon(probe, poly) ? normal : { x: -normal.x, y: -normal.y };
			dimensions.push({ from, to, normal: inward });
		}
	}
	return dimensions;
};

/** Габарит чертежа по наружным граням стен, а не по осям. null — стен нет. */
export const outerBounds = (outlines: Record<string, WallOutline>) => {
	const points = Object.values(outlines).flat();
	if (points.length === 0) return null;
	return {
		minX: Math.min(...points.map((p) => p.x)),
		minY: Math.min(...points.map((p) => p.y)),
		maxX: Math.max(...points.map((p) => p.x)),
		maxY: Math.max(...points.map((p) => p.y)),
	};
};
